import { Close } from "@mui/icons-material";
import { Box, Button, Divider, Grid, Stack } from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFlagCheckered } from "react-icons/fa";
import { MdOutlineQueryStats } from "react-icons/md";
import { SlNote } from "react-icons/sl";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { InfoBubble } from "../../..";
import { useWebSocket } from "../../../../contexts/WebSocketContext";
import { startAnalysis } from "../../../../pages/postEditor/seoAnalyzer/plan";
import { useGeneratePostOutlineMutation } from "../../../../redux/slices/featuresApiSlice";
import { setActivePanel } from "../../../../redux/slices/panelSlice";
import {
  useGetAllCompanyVariablesQuery,
  useGetAllWritingStylesQuery,
} from "../../../../redux/slices/profileApiSlice";
import { dispatch } from "../../../../redux/store";
import { getSessionUUID } from "../../../../utils/uuidManager";
import { CompanyModal, WritingStyleModal } from "../../../generationVariables";
import GlobalNumberField from "../../FeaturesTemplate/FeaturesFields/GlobalNumberField";
import GlobalTextField from "../../FeaturesTemplate/FeaturesFields/GlobalTextField";
import { LaunchAnalysisBtn } from "../../Others";
import { OutlineEditorTagSelect } from "../../Toolbar/TypographySelect";
import { globalValidationConfig } from "../formik-function/helpers";
import RewriteModal from "../global-rewrite-modal/rewrite-modal";
import "./styles.css";

import { setCompetitorAnalysis } from "../../../../redux/slices/competitorSlice";
import {
  useGetCompetitorAnalysisMutation,
  useGetLinkTableMutation,
} from "../../../../redux/slices/postEditorBlogSlice";

import { LoadingButton } from "@mui/lab";
import { Tooltip } from "@mui/material";
import Document from "@tiptap/extension-document";
import { setCredentials } from "../../../../redux/slices/authSlice";
import { useGenerationErrorHandling } from "../../../errorHandling";
import { trackPlatformUsed } from "../../../trackers";
import { GenerationButton } from "../../FeaturesTemplate/FeaturesFields/GenerationButton";

const Outline = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state?.auth.userInfo);
  const accessToken = useSelector((state) => state?.auth).accessToken;
  const {
    analysisState,
    editorRef,
    openOutlineRewrite,
    setOpenOutlineRewrite,
    bufferRef,
    cleanGeneratedContent,
  } = useWebSocket();
  const [customize, setCustomize] = useState(false);

  const [generatePostOutline, { isLoading: outlineGenerating }] =
    useGeneratePostOutlineMutation();
  const handleGenerationError = useGenerationErrorHandling();
  const formikProps = useFormikContext();

  const [competitorAnalysis, { isLoading: analyzing }] =
    useGetCompetitorAnalysisMutation();
  const open = useSelector((state) => state.panelSlice.openOutline);
  const competitorResults = useSelector(
    (state) => state.competitorAnalysis.data?.data
  );

  const currentProject = useSelector((state) => state?.project);
  const isInitialized = currentProject?.isInitialized;
  const website_url = currentProject?.project?.id
    ? currentProject?.project?.id
    : null;

  const { data: writingStyles, isLoading: writingLoading } =
    useGetAllWritingStylesQuery(
      { website_url },
      {
        skip: isInitialized === false && currentProject?.project != null,
      }
    );
  const { data: companies, isLoading: companyLoading } =
    useGetAllCompanyVariablesQuery(
      { website_url },
      {
        skip: isInitialized === false && currentProject?.project != null,
      }
    );
  // const { data: products, isLoading: productLoading } = useGetAllProductVariableQuery();
  const [writingOpen, setWritingOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  // const [productOpen, setProductOpen] = useState(false);
  const handleWritingOpen = () => {
    setWritingOpen(true);
  };
  const [linkingTable, { isLoading: loadingTable }] = useGetLinkTableMutation();
  const handleCompanyOpen = () => {
    setCompanyOpen(true);
  };
  // const handleProductOpen = () => {setProductOpen(true);};
  const handleCompanyClose = () => setCompanyOpen(false);
  const handleWritingClose = () => setWritingOpen(false);
  // const handleProductClose = () => setProductOpen(false);
  const [analysis, setAnalysis] = useState();
  const [check, setCheck] = useState({
    introduction: false,
    conclusion: false,
  });

  const CustomDocument = Document.extend({
    content: "heading+",
  });

  const editor = useEditor({
    extensions: [
      CustomDocument,
      StarterKit.configure({
        document: false,
        heading: { levels: [2, 3, 4, 5, 6] },
      }),
    ],
    content: "",
    onCreate: ({ editor }) => {
      editor.view.dom.innerHTML = formikProps.values.outline ?? "";
      editorRef.current = editor;
    },
    onUpdate: ({ editor }) => {
      if (editor.view.dom.textContent !== "") {
        formikProps.setFieldValue("outline", editor.view.dom.innerHTML);
      } else {
        formikProps.setFieldValue("outline", "");
      }
    },
  });

  useEffect(() => {
    if (editor && formikProps.values.outline !== editor.getHTML()) {
      // editor.commands.setContent();
    }
    const analyze = startAnalysis(formikProps);
    setAnalysis(analyze);
  }, [formikProps.values.outline, editor, formikProps.values.nb_words]);
  const loadLinkingTable = async () => {
    const payload = {
      text:
        formikProps.values.introduction +
        formikProps.values.content +
        formikProps.values.conclusion,
      domain: formikProps.values.url_path,
    };
    const res = await linkingTable(payload).unwrap();
    if (res?.table) {
      dispatch(
        setCompetitorAnalysis({
          data: {
            ...competitorResults,
            linking: res,
          },
        })
      );
    }
  };
  const handleAnalysis = async () => {
    trackPlatformUsed("blog_post", "seo_analysis");
    if (
      userInfo.seo_non_perishing === 0 &&
      userInfo.seo_analysis_tokens === 0
    ) {
      toast.error(
        t(
          "no-seo-tokens",
          "No SEO analysis tokens remaining.Upgrade subscription or buy SEO token pack"
        )
      );
    } else if (formikProps.values.seo_request.trim().length == 0) {
      toast.error(t("seo-request-is-required", "SEO request is required"));
    } else {
      try {
        const payload = {
          post_id: formikProps.values.id,
          seo_request: formikProps.values.seo_request,
          dummy: process.env.REACT_APP_ISDUMMY === "true",
        };
        const res = await competitorAnalysis(payload).unwrap();

        if (res?.status === 201) {
          dispatch(setCompetitorAnalysis({ data: res?.data }));
          dispatch(
            setCredentials({
              accessToken: accessToken,
              userInfo: {
                ...userInfo,
                seo_analysis_tokens: res?.seo_analysis_tokens,
                seo_non_perishing: res?.seo_non_perishing,
              },
            })
          );
          toast.info("seo-token-used", "1 SEO analysis token used");
        }
        formikProps.setFieldValue("selected_seo_analysis_id", res?.data?.id);
        // loadLinkingTable();
      } catch (err) {
        toast.error(t("error-retrieving-data", "Error while retrieving data"));
      }
    }
  };

  const handleWriteOutline = () => {
    let editorContent = editor.getHTML();

    const parser = new DOMParser();
    const doc = parser.parseFromString(editorContent, "text/html");

    let introduction = "";
    let conclusion = "";
    let remainingHtml = editorContent;

    const h2Tags = doc.querySelectorAll("h2");

    if (h2Tags.length > 0) {
      // Check if the first <h2> is "Introduction"
      if (h2Tags[0].innerText.trim().toLowerCase() === "introduction") {
        introduction += "<h2>Introduction</h2>";
        let nextSibling = h2Tags[0].nextSibling;
        while (nextSibling && !(nextSibling.tagName === "H2")) {
          introduction += nextSibling.outerHTML || nextSibling.textContent;
          nextSibling = nextSibling.nextSibling;
        }
        remainingHtml = remainingHtml.replace(introduction, "");
      }

      // Check if the last <h2> is "Conclusion"
      if (
        h2Tags[h2Tags.length - 1].innerText.trim().toLowerCase() ===
        "conclusion"
      ) {
        conclusion += "<h2>Conclusion</h2>";
        let nextSibling = h2Tags[h2Tags.length - 1].nextSibling;
        while (nextSibling) {
          conclusion += nextSibling.outerHTML || nextSibling.textContent;
          nextSibling = nextSibling.nextSibling;
        }
        remainingHtml = remainingHtml.replace(conclusion, "");
      }
    }

    // Insert a new empty paragraph before every <h2> (except the first) to ensure clean line breaks
    remainingHtml = remainingHtml.replace(/(<h2>)/g, (match, p1, offset) => {
      return offset === 0 ? p1 : `<p></p>${p1}`;
    });

    const contentzone = document.getElementById("contentzone");
    const introzone = document.getElementById("introzone");
    const conclusionzone = document.getElementById("conclusionzone");

    if (contentzone) contentzone.innerHTML = remainingHtml;
    if (introzone) introzone.innerHTML = introduction;
    if (conclusionzone) conclusionzone.innerHTML = conclusion;

    handleClose();
  };

  const handleGenerateOultine = async () => {
    trackPlatformUsed("outline", "ai_generation");
    try {
      const payload = {
        id:
          formikProps.values.id === null || formikProps.values.id === ""
            ? null
            : formikProps.values.id,
        style_id:
          formikProps.values.style_id === null ||
          formikProps.values.style_id === ""
            ? null
            : formikProps.values.style_id,
        company_id:
          formikProps.values.company_id === null ||
          formikProps.values.company_id === ""
            ? null
            : formikProps.values.company_id,
        product_id:
          formikProps.values.product_id === null ||
          formikProps.values.product_id === ""
            ? null
            : formikProps.values.product_id,
        subject: formikProps.values.subject,
        seo_request: formikProps.values.seo_request,
        is_h1_tag: false,
        smart_redaction: formikProps.values.smart_redaction_outline,
        manually_choose_nb_words: formikProps.values.manually_choose_nb_words,
        // If manual
        ...(!formikProps.values.smart_redaction_outline && {
          nb_words: formikProps.values.nb_words,
          nb_parts: formikProps.values.nb_parts,
          depth: formikProps.values.depth,
        }),
        // If smart redaction
        ...(formikProps.values.smart_redaction_outline && {
          seo_analysis_id: formikProps.values.selected_seo_analysis_id,
        }),
      };
      if (formikProps.values.outline !== "") {
        payload["outline"] = formikProps.values.outline;
      }
      // console.log("generate oultine payload: ", payload);
      // ✅ Add customize instruction if enabled
      if (customize && formikProps.values.customize_prompt) {
        payload["customize_prompt"] = formikProps.values.customize_prompt;
      }
      const uuid = userInfo?.uuid || getSessionUUID();
      const res = await generatePostOutline({
        ...payload,
        uuid,
        stream: true,
      }).unwrap();

      // console.log(res?.data);

      if (res?.status !== 200) {
        toast.error("Error while generating oultine");
        return;
      }

      if (formikProps.values.outline === "") {
        formikProps.setFieldValue("outline", cleanGeneratedContent(res?.data));
        editor.view.dom.innerHTML = cleanGeneratedContent(res?.data);
        if (
          !formikProps.values.manually_choose_nb_words &&
          formikProps.values.smart_redaction_outline
        ) {
          formikProps.setFieldValue("nb_words", res?.nb_words);
        }
      } else {
        formikProps.setFieldValue(
          "outline_rewrite",
          cleanGeneratedContent(res?.data)
        );
        setOpenOutlineRewrite(true);
      }

      bufferRef.current = "";
    } catch (error) {
      handleGenerationError(error);
    }
  };

  const saveSelectedVersion = (data) => {
    editor.view.dom.innerHTML = data;
    //formikProps.setFieldValue('outline', data);
    formikProps.setFieldValue("outline_rewrite", "");
    setOpenOutlineRewrite(false);
  };

  const handleClose = () => {
    dispatch(setActivePanel(null));
    formikProps.setFieldValue("customize_prompt", "");
    formikProps.setFieldTouched("customize_prompt", false);
    window.scroll(0, 0);
  };

  useEffect(() => {
    if (!formikProps.values.is_manual_depth_and_length) {
      let nb_parts;
      let depth;
      const nb_words = formikProps.values.nb_words;
      if (nb_words < 500) {
        nb_parts = 2;
        depth = 2;
      } else if (nb_words < 1000) {
        nb_parts = 3;
        depth = 3;
      } else if (nb_words < 1500) {
        nb_parts = 4;
        depth = 3;
      } else if (nb_words < 2000) {
        nb_parts = 5;
        depth = 3;
      } else if (nb_words < 2500) {
        nb_parts = 5;
        depth = 4;
      } else if (nb_words < 3000) {
        nb_parts = 6;
        depth = 4;
      } else if (nb_words < 4000) {
        nb_parts = 7;
        depth = 4;
      } else if (nb_words < 5000) {
        nb_parts = 8;
        depth = 4;
      } else if (nb_words < 6000) {
        nb_parts = 8;
        depth = 5;
      } else {
        nb_parts = 8 + Math.round((nb_words - 5000) / 1000);
        depth = 5;
      }
      formikProps.setFieldValue("nb_parts", nb_parts);
      formikProps.setFieldValue("depth", depth);
    }
  }, [
    formikProps.values.nb_words,
    formikProps.values.is_manual_depth_and_length,
  ]);

  return (
    <Box
      sx={{
        position: "fixed",
        overflow: "hidden",
        height: { md: "100vh", xs: "100%" },
        width: "100%",
        display: "flex",
        flexDirection: { md: "row", xs: "column" },
        mt: { md: "48px !important", xs: "unset" },
        left: 0,
        top: 0,
        bgcolor: "#000000a1",
        zIndex: 9999,
      }}
    >
      {/* Modal */}
      <Box
        sx={{
          width: {
            lg: "calc(100vw - 500px)",
            md: "calc(100vw - 350px)",
            xs: "100%",
          },
          display: "flex",
          minHeight: "100vh", // ✅ Ensures it doesn't shrink unexpectedly
          height: "100%", // ✅ Instead of "100%"
          overflow: { md: "auto", xs: "unset" },
        }}
        className="overflow-y-scroll"
      >
        <Box
          sx={{
            height: {
              xl: "98vh",
              lg: "96vh",
              md: "94vh",
              xs: "100vh",
            },
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pb: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              position: "relative",
              height: "100%",
              bgcolor: "white",
              // maxWidth: "900px",
              // maxHeight: "900px",
              width: "100%",
              borderRadius: 0,
              m: "auto",
            }}
          >
            <Box
              sx={{
                py: 2,
                px: 3,
                position: "relative",
                borderBottom: "3px solid #e5e7eb",
              }}
            >
              <p className="font-semibold">{t("Settings", "Réglages")}</p>
              <Box
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  cursor: "pointer",
                  right: 0,
                  top: 0,
                  p: 1,
                }}
              >
                <Close sx={{ color: "black" }} />
              </Box>
            </Box>
            <Box
              sx={{
                px: 3,
                py: 2,
                flex: 1,
                overflow: "auto",
                bgcolor: "white",
              }}
            >
              <Stack direction={{ md: "row", xs: "column" }} mb={2} spacing={1}>
                <Button
                  onClick={handleWritingOpen}
                  startIcon={<SlNote />}
                  sx={{
                    flex: 1,
                    bgcolor: formikProps.values.style_id
                      ? "#7ddb9f"
                      : "#e5e5e5",
                    borderRadius: 2,
                    textAlign: "center",
                    textTransform: "none",
                    color: "black",
                  }}
                >
                  {t("Writing-style", "Style d'écriture")}{" "}
                  {!formikProps.values.style_id &&
                    writingStyles?.data?.some(
                      (style) => style.id === formikProps.values.style_id
                    ) &&
                    "(Optional)"}
                </Button>

                <WritingStyleModal
                  open={writingOpen}
                  handleClose={handleWritingClose}
                  formikProps={formikProps}
                  writingStyles={writingStyles?.data}
                />

                <Button
                  onClick={handleCompanyOpen}
                  startIcon={<SlNote />}
                  sx={{
                    flex: 1,
                    bgcolor: formikProps.values.company_id
                      ? "#7ddb9f"
                      : "#e5e5e5",
                    borderRadius: 2,
                    textAlign: "center",
                    textTransform: "none",
                    color: "black",
                  }}
                >
                  {t("Brand-identity", "Identité de marque")}{" "}
                  {!formikProps.values.company_id &&
                    companies?.data?.some(
                      (company) => company.id === formikProps.values.company_id
                    ) &&
                    "(Optional)"}
                </Button>

                <CompanyModal
                  open={companyOpen}
                  handleClose={handleCompanyClose}
                  formikProps={formikProps}
                  companyStyles={companies?.data}
                />

                {/* <Button
                        startIcon={<SlNote />}
                        sx={{
                          flex: 1,
                          bgcolor: '#e5e5e5',
                          borderRadius: 2,
                          textAlign: 'center',
                          textTransform: 'none',
                          color: 'black',
                        }}
                      >
                        Product
                      </Button> */}
              </Stack>

              <Grid container spacing={4}>
                <Grid item lg={6} xs={12}>
                  <p>
                    {t("Subject-of-the-post", "Sujet de l'article")}
                    <span className="text-red-500 ml-1">*</span>
                  </p>
                  <GlobalTextField
                    formikProps={formikProps}
                    name={"subject"}
                    placeholder={
                      "Ex: " +
                      t("blog-post-guide-seo", "un guide complet SEO sur ...")
                    }
                    isRequired={globalValidationConfig.subject.required}
                    multipleLines={true}
                    maxLength={globalValidationConfig.subject.max}
                  />
                </Grid>
                <Grid item lg={6} xs={12}>
                  <p>
                    {t("SEO-target-request", "Requête SEO cible")}
                    <span className="text-red-500 ml-1">*</span>
                  </p>
                  <GlobalTextField
                    formikProps={formikProps}
                    name={"seo_request"}
                    placeholder={
                      "Ex: " + t("seo-request-guide-seo", "article seo ia")
                    }
                    isRequired={globalValidationConfig.seo_request.required}
                    maxLength={globalValidationConfig.seo_request.max}
                  />
                  <div className="relative flex flex-col justify-center items-center group">
                    <p className="text-xs font-bold">
                      {t("available-tokens", "Available Tokens")}:{" "}
                      {(userInfo?.seo_analysis_tokens || 0) +
                        (userInfo?.seo_non_perishing || 0)}
                    </p>
                    {!analysisState ? (
                      !analyzing ? (
                        <LoadingButton
                          startIcon={<MdOutlineQueryStats />}
                          onClick={handleAnalysis}
                          variant="contained"
                          loading={analyzing}
                          className="text-sm !bg-pink-600 hover:!bg-pink-700 *: text-white disabled:bg-slate-200 disabled:text-slate-400 font-light px-5 py-[6px] rounded flex items-center"
                          disabled={false}
                          sx={{
                            textTransform: "none",
                          }}
                        >
                          {t("Launch-analysis", " Lancer l'analyse SEO")}
                        </LoadingButton>
                      ) : (
                        <button
                          type="button"
                          class="gap-2 text-sm !bg-pink-600 hover:!bg-pink-700 *: text-white disabled:bg-slate-200 disabled:text-slate-100 font-light px-5 py-[6px] rounded flex items-center"
                          disabled
                        >
                          <div class="w-5 h-5 border-2 border-slate-100 border-t-transparent rounded-full animate-spin"></div>
                          {t("grabbing-competitors")}
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        class="gap-2 text-sm !bg-pink-600 hover:!bg-pink-700 *: text-white disabled:bg-slate-200 disabled:text-slate-100 font-light px-5 py-[6px] rounded flex items-center"
                        disabled
                      >
                        <div class="w-5 h-5 border-2 border-slate-100 border-t-transparent rounded-full animate-spin"></div>
                        {analysisState}
                      </button>
                    )}
                  </div>
                </Grid>

                <Grid item lg={12} xs={12}>
                  <Divider />
                </Grid>

                <Grid item lg={4} xs={12}>
                  <p>{t("Outline", "Plan")}</p>

                  <Tooltip
                    title={
                      !formikProps.values.selected_seo_analysis_id
                        ? t(
                            "you-must-start-competitor-analysis-first",
                            "Vous devez d'abord lancer une analyse de la concurrence"
                          )
                        : ""
                    }
                    slotProps={{
                      popper: {
                        sx: { zIndex: 99999999 },
                      },
                    }}
                  >
                    <div className="mt-4 inline-flex items-center h-6">
                      <label className="inline-flex items-center cursor-pointer relative">
                        <input
                          type="checkbox"
                          name="smart_redaction_outline"
                          checked={formikProps.values.smart_redaction_outline}
                          onChange={formikProps.handleChange}
                          disabled={
                            !formikProps.values.selected_seo_analysis_id
                          }
                          className="sr-only peer"
                        />
                        <div
                          className={`relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 transition-colors duration-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:start-[2px] ${
                            formikProps.values.selected_seo_analysis_id
                              ? "after:bg-white"
                              : "after:bg-slate-200"
                          } after:rounded-full after:h-5 after:w-5 after:transition-transform after:box-border`}
                        ></div>
                      </label>
                      <span
                        className={`ms-3 text-sm ${
                          !formikProps.values.selected_seo_analysis_id &&
                          "text-gray_local-400"
                        }`}
                      >
                        Rédaction intelligente via les plans concurrents ?
                      </span>
                    </div>
                  </Tooltip>

                  {formikProps?.values?.smart_redaction_outline && (
                    <div className="mt-4 inline-flex items-center h-6">
                      <label className="inline-flex items-center cursor-pointer relative">
                        <input
                          type="checkbox"
                          name="manually_choose_nb_words"
                          checked={formikProps.values.manually_choose_nb_words}
                          onChange={formikProps.handleChange}
                          disabled={
                            !formikProps.values.selected_seo_analysis_id
                          }
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 transition-colors duration-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:box-border"></div>
                      </label>
                      <span className="ms-3 text-sm flex items-center gap-1">
                        Choisir le nombre de mots de l'article ?{" "}
                        <InfoBubble
                          placement="top-center"
                          width={250}
                          text="Sinon, un nombre de mots sera automatiquement proposé lors de la génération du plan"
                        />
                      </span>
                    </div>
                  )}

                  <Box>
                    <GlobalNumberField
                      formikProps={formikProps}
                      name={"nb_words"}
                      text={`${t("Nb-words", "Nombre de mots dans l'article")}
                
                   ${
                     competitorResults?.general &&
                     `<div class='text-sm '>
                    <span class="text-black font-bold">${t(
                      "Competitors",
                      "Competitors"
                    )}: </span>
                    <span class='text-red-600 font-bold'>
                    ${competitorResults?.general.content_length.low}

                    </span>
                    -
                    <span class='text-yellow-500 font-bold'>
                    ${competitorResults?.general.content_length.medium}

                    </span>
                    -
                    <span class='text-green-500 font-bold'>
                    ${competitorResults?.general.content_length.high}

                    </span>
                    </div>`
                   }
                   
                    `}
                      isRequired={globalValidationConfig.nb_words.required}
                      minValue={globalValidationConfig.nb_words.min}
                      maxValue={globalValidationConfig.nb_words.max}
                      step={100}
                      disabled={
                        !formikProps.values.manually_choose_nb_words &&
                        formikProps.values.smart_redaction_outline
                      }
                    />
                  </Box>

                  {!formikProps?.values?.smart_redaction_outline && (
                    <div>
                      <div className="mt-4 inline-flex items-center h-6">
                        <label className="inline-flex items-center cursor-pointer relative">
                          <input
                            type="checkbox"
                            name="is_manual_depth_and_length"
                            checked={
                              formikProps.values.is_manual_depth_and_length
                            }
                            onChange={formikProps.handleChange}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 transition-colors duration-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:box-border"></div>
                        </label>
                        <span className="ms-3 text-sm">
                          Choisir nombre et profondeur manuellement?
                        </span>
                      </div>

                      <GlobalNumberField
                        formikProps={formikProps}
                        name={"nb_parts"}
                        text={t(
                          "Nb-big-parts",
                          "Nombre de grandes parties (H2)"
                        )}
                        isRequired={globalValidationConfig.nb_parts.required}
                        minValue={globalValidationConfig.nb_parts.min}
                        maxValue={globalValidationConfig.nb_parts.max}
                        disabled={
                          !formikProps.values.is_manual_depth_and_length
                        }
                      />

                      <GlobalNumberField
                        formikProps={formikProps}
                        name={"depth"}
                        text={t("Nb-depth-parts", "Profondeur de parties")}
                        isRequired={globalValidationConfig.depth.required}
                        minValue={globalValidationConfig.depth.min}
                        maxValue={globalValidationConfig.depth.max}
                        disabled={
                          !formikProps.values.is_manual_depth_and_length
                        }
                      />
                    </div>
                  )}
                  {/* Customize toggle */}
                  <div className="mt-4 inline-flex items-center h-6">
                    <label className="inline-flex items-center cursor-pointer relative">
                      <input
                        type="checkbox"
                        name="customize"
                        checked={customize}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setCustomize(checked);

                          // ✅ clear input when turning off
                          if (!checked) {
                            formikProps.setFieldValue("customize_prompt", "");
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 transition-colors duration-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:box-border"></div>
                    </label>
                    <span className="ms-3 text-sm">
                      {t("custom-instructions", "Instructions personnalisées")}
                    </span>
                  </div>

                  {/* Customize field */}
                  {customize && (
                    <Grid item lg={12} xs={12}>
                      <p>
                        {t("Instruction", "Instruction")}
                        <span className="text-red-500 ml-1">*</span>
                      </p>
                      <GlobalTextField
                        formikProps={formikProps}
                        name={"customize_prompt"}
                        placeholder={t(
                          "Ex: Make it more formal",
                          "Ex: Rends-le plus formel"
                        )}
                        isRequired={true}
                        multipleLines={true}
                        maxLength={1000}
                      />
                    </Grid>
                  )}
                  {/* <pre>{JSON.stringify(analysisState, null, 2)}</pre> */}

                  <GenerationButton
                    formikProps={formikProps}
                    text={t("Generate-outline", "Générer le plan")}
                    handleGenerate={handleGenerateOultine}
                    isLoading={outlineGenerating}
                    additionalSx={{ my: 3 }}
                    platform="openai"
                    customize={customize}
                  />
                </Grid>

                <Grid item lg={8} xs={12}>
                  {editor ? (
                    <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
                      <OutlineEditorTagSelect editor={editor} />
                      <EditorContent className="mini-editor" editor={editor} />
                    </Box>
                  ) : null}

                  {formikProps.values.outline_rewrite !== "" ? (
                    <Button
                      variant="contained"
                      className="!bg-violet-400 hover:!bg-violet-500 text-white !normal-case"
                      onClick={() => setOpenOutlineRewrite(true)}
                    >
                      {t("View-rewritten-version", "Voir la version réécrite")}
                    </Button>
                  ) : null}

                  {openOutlineRewrite ? (
                    <div className="fixed overflow-auto w-full mui-md:w-[calc(100vw - 500px)] z-[99999] mt-0 mui-md:mt-[48px] top-0 bg-[#00000029] left-0 flex   h-screen">
                      <RewriteModal
                        previous={formikProps.values.outline}
                        rewritten={formikProps.values.outline_rewrite}
                        label={t("Outline-rewrite", "Réécriture du plan")}
                        save={saveSelectedVersion}
                        setOpenModal={setOpenOutlineRewrite}
                        rewriteFieldName="outline_rewrite"
                      />
                    </div>
                  ) : null}
                </Grid>

                <Grid
                  item
                  sx={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "center",
                  }}
                  lg={12}
                  xs={12}
                  className="pb-0 md:pb-3 xl:pb-6 xxl:pb-0"
                >
                  <Button
                    startIcon={<FaFlagCheckered />}
                    sx={{ textTransform: "none" }}
                    variant="contained"
                    onClick={handleWriteOutline}
                  >
                    {t("Start-with-this-outline", "Commencer avec ce plan")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Panel */}
      <Box
        sx={{
          backgroundColor: "#303539",
          minHeight: "100vh",
          height: "100%",
          overflow: "auto",
          width: { lg: "500px", md: "350px", xs: "100%" },
          color: "white",
          pb: 5,
        }}
        className="overflow-y-scroll"
      >
        <Box
          sx={{
            backgroundColor: "#9b4843",
            p: 2,
            position: "relative",
          }}
        >
          <Box
            onClick={handleClose}
            sx={{
              position: "absolute",
              cursor: "pointer",
              right: 0,
              top: 0,
              p: 1,
            }}
          >
            <Close sx={{ color: "white" }} />
          </Box>
          <h1 className="text-2xl mb-2">{t("Outline", "Plan")}</h1>

          <p className="text-xs mb-2">
            {t(
              "tutorial-text-outline",
              "La première étape dans la rédaction de votre contenu est d’établir un bon plan. Suivez les indications et comparez-vous à vos concurrents pour réaliser le meilleur plan possible !"
            )}
          </p>

          {/* <p className="text-xs">
            {t("For-more-info:", "Pour plus d'infos :")}{" "}
            <span>
              <a className="text-blue-500 underline cursor-pointer">
                {
                  ("outline-optimization-guide",
                  "Guide complet pour réaliser le meilleur plan SEO en 2024")
                }
              </a>
            </span>
          </p> */}
        </Box>

        <LaunchAnalysisBtn
          userInfo={userInfo}
          formikProps={formikProps}
          analysis={analysis}
          disabled={true}
          type={"plan"}
        />
      </Box>
    </Box>
  );
};

export default Outline;
